import { useState, useRef } from 'react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { meetingsAPI } from '../../lib/api';
import { apiErrorText } from './meetingsUtils';

export default function CompanyPicker({ value, onChange, label = 'Company', testIdPrefix = 'company-picker', allowUntagged = true }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [newName, setNewName] = useState('');
  const [newCountry, setNewCountry] = useState('');
  const [newPotentialType, setNewPotentialType] = useState('CUSTOMER');
  const [creating, setCreating] = useState(false);
  const [showUntagged, setShowUntagged] = useState(false);
  const [untaggedName, setUntaggedName] = useState('');
  const debounceRef = useRef(null);
  const requestSeq = useRef(0);

  const search = (q) => {
    setQuery(q);
    clearTimeout(debounceRef.current);
    if (q.length < 2) { setResults([]); return; }
    debounceRef.current = setTimeout(async () => {
      const seq = ++requestSeq.current;
      try {
        const res = await meetingsAPI.searchCompanies(q);
        if (seq === requestSeq.current) setResults(res.data.companies || []);
      } catch { if (seq === requestSeq.current) setResults([]); }
    }, 300);
  };

  const createProspect = async () => {
    if (!newName.trim() || !newCountry.trim()) { toast.error('Name and country are required'); return; }
    setCreating(true);
    try {
      const res = await meetingsAPI.createProspect({ name: newName.trim(), country: newCountry.trim(), potential_type: newPotentialType });
      onChange({ type: 'PROSPECT', ref: res.data.company_ref, name: res.data.display_name, country: res.data.country, potential_type: newPotentialType });
      setShowNew(false); setNewName(''); setNewCountry(''); setNewPotentialType('CUSTOMER'); setResults([]); setQuery('');
    } catch (e) { toast.error(apiErrorText(e, 'Failed to create company')); }
    finally { setCreating(false); }
  };

  // Round 3 (31 Oct 2026, Visits & Meetings): no lookup, no data shown — "Visits to
  // tag" lets the recorder, their manager chain or Admin resolve this to a real
  // company later.
  const useUntagged = () => {
    const n = untaggedName.trim();
    if (!n) { toast.error('Please type the company name'); return; }
    onChange({ type: 'UNTAGGED', ref: n, name: n });
    setShowUntagged(false); setUntaggedName(''); setResults([]); setQuery('');
  };

  if (value) {
    return (
      <div className="space-y-1 min-w-0">
        <Label>{label}</Label>
        <div className="flex items-center gap-2 text-sm border rounded-sm px-3 py-2 min-w-0" data-testid={`${testIdPrefix}-selected`}>
          <span className="flex-1 truncate min-w-0">{value.name}</span>
          {value.type === 'PROSPECT' && <Badge variant="outline" className="text-[10px]">New / Unmatched</Badge>}
          {value.type === 'UNTAGGED' && <Badge variant="outline" className="text-[10px] border-dashed">Untagged — tag later</Badge>}
          <Button size="sm" variant="ghost" data-testid={`${testIdPrefix}-clear-btn`} onClick={() => onChange(null)}>Change</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-1">
      <Label>{label}</Label>
      <Input placeholder="Search vendor, customer or prospect..." value={query} onChange={(e) => search(e.target.value)}
        data-testid={`${testIdPrefix}-search-input`} />
      {results.length > 0 && (
        <div className="border rounded-sm divide-y max-h-40 overflow-y-auto">
          {results.map((r, i) => (
            <button key={i} type="button" className="w-full text-left px-3 py-2 text-sm hover:bg-muted/60"
              data-testid={`${testIdPrefix}-option-${i}`} onClick={() => { onChange(r); setResults([]); setQuery(''); }}>
              {r.name} <span className="text-muted-foreground text-xs">({r.type}{r.country ? `, ${r.country}` : ''})</span>
            </button>
          ))}
        </div>
      )}
      {!showNew ? (
        <div className="flex flex-wrap gap-2">
          <Button type="button" size="sm" variant="outline" className="rounded-sm" data-testid={`${testIdPrefix}-new-btn`}
            onClick={() => { setShowNew(true); setNewName(query); }}>
            + Add a new company not listed
          </Button>
          {!showUntagged && allowUntagged ? (
            <Button type="button" size="sm" variant="ghost" className="rounded-sm text-muted-foreground" data-testid={`${testIdPrefix}-untagged-btn`}
              onClick={() => { setShowUntagged(true); setUntaggedName(query); }}>
              Don't know the company? Just type the name
            </Button>
          ) : null}
        </div>
      ) : (
        <div className="border rounded-sm p-2 space-y-2" data-testid={`${testIdPrefix}-new-form`}>
          <div className="flex items-end gap-2">
            <div className="flex-1">
              <Label className="text-xs">Company name</Label>
              <Input value={newName} onChange={(e) => setNewName(e.target.value)} data-testid={`${testIdPrefix}-new-name`} />
            </div>
            <div className="flex-1">
              <Label className="text-xs">Country</Label>
              <Input value={newCountry} onChange={(e) => setNewCountry(e.target.value)} placeholder="e.g. China" data-testid={`${testIdPrefix}-new-country`} />
            </div>
          </div>
          <div>
            <Label className="text-xs">A new company — discussed as</Label>
            <div className="flex gap-1.5 mt-1">
              {[['CUSTOMER', 'Potential customer'], ['VENDOR', 'Potential vendor'], ['BOTH', 'Both']].map(([v, l]) => (
                <button key={v} type="button" onClick={() => setNewPotentialType(v)} data-testid={`${testIdPrefix}-new-potential-${v}`}
                  className={`px-2.5 py-1 text-xs rounded-sm border ${newPotentialType === v ? 'bg-foreground text-background border-foreground' : 'bg-background'}`}>
                  {l}
                </button>
              ))}
            </div>
          </div>
          <div className="flex justify-end">
            <Button size="sm" disabled={creating} onClick={createProspect} data-testid={`${testIdPrefix}-new-save-btn`}>Add</Button>
          </div>
        </div>
      )}
      {showUntagged && (
        <div className="border border-dashed rounded-sm p-2 space-y-2" data-testid={`${testIdPrefix}-untagged-form`}>
          <Label className="text-xs">Company name (typed, no lookup — tag it to a real company later)</Label>
          <div className="flex items-end gap-2">
            <Input className="flex-1" value={untaggedName} onChange={(e) => setUntaggedName(e.target.value)} data-testid={`${testIdPrefix}-untagged-name`} />
            <Button size="sm" variant="outline" onClick={useUntagged} data-testid={`${testIdPrefix}-untagged-save-btn`}>Use this name</Button>
          </div>
        </div>
      )}
    </div>
  );
}
